import React, {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react'
import {
  Box,
  Divider,
  InputAdornment,
  Switch,
  TextField as MuiTextField,
  Typography
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { Status } from '../../../hooks/useFetchState'
import { useApi } from '../../../hooks/useApi'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { SnackBar } from '../../SnackBar/Snackbar'
import Grid from '@mui/material/Grid2'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import { positionValidationSchema } from '../types/positionConfig'
import {
  IEditPosition,
  PositionAttributeDto,
  PositionDetailInfoDto
} from '../../../interfaces/Position/Position'
import { Zone } from '../../../interfaces'
import { useWarehouses, useZones } from '../../../hooks'
import { eTypePosition } from '../../../enums/eTypePosition'
import { createPosition, getTypePositionsDDL, updatePosition } from '../api'
import { GenericDLL } from '../../Almacen/UI/GenericDLL'
import { Loading } from '../../UI'
import CustomAlert from '../../_WMS/AlertMessage/AlertMessage'

export interface CreatePositionHandle {
  submitForm: () => void
  isSubmitting: boolean
  isValid: boolean
  isLoading: boolean
}

interface CreatePositionProps {
  editableItem?: PositionDetailInfoDto
  onSuccess?: (message: string) => void
}

interface SwitchRowProps {
  label: string
  description: string
  checked: boolean
  onChange: (checked: boolean) => void
}

const SwitchRow: React.FC<SwitchRowProps> = ({
  label,
  description,
  checked,
  onChange
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.5
    }}
  >
    <Box>
      <Typography variant='body2' fontWeight={600}>
        {label}
      </Typography>
      <Typography variant='caption' color='text.secondary'>
        {description}
      </Typography>
    </Box>
    <Switch
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      size='small'
    />
  </Box>
)

const defaultAttributes: PositionAttributeDto = {
  monoSku: false,
  monoBatch: false,
  blockIngress: false,
  blockEgress: false,
  maxCapacity: 0
}

const initialValues = {
  id: 0,
  warehouseId: '' as unknown as number,
  positionName: '',
  positionDescription: '',
  subZoneId: undefined as number | undefined,
  zoneId: undefined as number | undefined,
  typePositionId: '' as unknown as number,
  active: true,
  externalStorage: false,
  aisle: '',
  column: '',
  level: '',
  slot: '',
  positionAttributes: { ...defaultAttributes } as PositionAttributeDto
}

const buildPositionName = (
  aisle: string,
  column: string,
  level: string,
  slot: string
): string => `${aisle}${column}${level}${slot}`.toUpperCase()

export const CreatePosition = forwardRef<
  CreatePositionHandle,
  CreatePositionProps
>(({ editableItem, onSuccess }, ref) => {
  const formikRef = useRef<any>(null)
  const {
    isActive,
    message,
    isSuccess: isSnackSuccess,
    openSnackBar
  } = useSnackbar()
  const { callApi, state: apiState } = useApi()
  const [requiredZone, setRequiredZone] = useState<boolean>(false)
  const [requiresLayout, setRequiresLayout] = useState<boolean>(false)
  const [zonesDDL, setZonesDDL] = useState<Zone[]>([])
  const [typePositionItems, setTypePositionItems] = useState<any[]>([])

  const isEditing = !!editableItem?.positionId
  const isLocked = !!editableItem?.hasStockMovements

  useImperativeHandle(
    ref,
    () => ({
      submitForm: () => formikRef.current?.submitForm(),
      get isSubmitting() {
        return formikRef.current?.isSubmitting ?? false
      },
      get isValid() {
        return formikRef.current?.isValid ?? false
      },
      get isLoading() {
        return apiState.status === Status.Loading
      }
    }),
    [apiState.status]
  )

  const {
    zonesState: { zones },
    getZones
  } = useZones()
  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  const handleSelectedWarehouseZones = (warehouseId: number, zones: Zone[]) => {
    if (warehouseId > 0) {
      setZonesDDL(
        [...zones!].filter((x: Zone) => x.warehouseId === warehouseId)
      )
    }
  }

  const validateSelectedTypePosition = (typePosition: number) => {
    if (typePosition > 0) {
      const match = typePositionItems.find(
        (x) => x.typePositionId === typePosition
      )
      const requireZone = match?.requireZone ?? false
      const layoutRequired = match?.requiresLayout ?? false
      setRequiredZone(requireZone)
      setRequiresLayout(layoutRequired)
      return { requireZone, requiresLayout: layoutRequired }
    }
    setRequiresLayout(false)
    return { requireZone: false, requiresLayout: false }
  }

  useEffect(() => {
    if (warehouses?.length === 0) getWarehouses()
    if (zones?.length === 0) getZones()
    getTypePositionsDDL()
      .then((response) => response.data)
      .then((data) => setTypePositionItems(data?.items ?? data))
  }, [])

  useEffect(() => {
    if (editableItem?.warehouseId && zones?.length) {
      handleSelectedWarehouseZones(editableItem.warehouseId, zones)
    }
  }, [editableItem, zones])

  useEffect(() => {
    if (editableItem?.typePositionId && typePositionItems.length) {
      validateSelectedTypePosition(Number(editableItem.typePositionId))
    }
  }, [editableItem?.typePositionId, typePositionItems])

  const handleZoneChange = (
    value: number,
    setFieldValue: (field: string, value: any) => void,
    zonesDDL: Zone[]
  ) => {
    setFieldValue('zoneId', value)
    setFieldValue('subZoneId', undefined)
    const selectedZone = zonesDDL.find((z) => z.zoneId === Number(value))
    setFieldValue(
      'externalStorage',
      selectedZone ? !!selectedZone.externalStorage : false
    )
  }

  const shouldShowTypePosition = (
    isExternalZone: boolean,
    typePositionId: number,
    isEditable: boolean
  ): boolean => {
    const isReserva = typePositionId === eTypePosition.Reserva
    return (
      (isExternalZone && isReserva && isEditable) || (!isReserva && isEditable)
    )
  }

  const isLoading =
    !warehouses?.length || !zones?.length || !typePositionItems.length

  const handleSubmit = async (values: any) => {
    await callApi(createPosition(values))
      .then(() => {
        onSuccess?.('Posición creada correctamente')
      })
      .catch((error) => openSnackBar(error, false))
  }

  const handleSubmitEdit = async (values: any) => {
    const payload: IEditPosition = {
      positionId: values.positionId,
      positionCode: values.positionCode,
      positionName: values.positionName,
      positionDescription: values.positionDescription,
      active: values.active,
      warehouseId: values.warehouseId,
      zoneId: values.zoneId,
      subZoneId: values.subZoneId,
      typePositionId: values.typePositionId,
      externalStorage: values.externalStorage,
      aisle: values.aisle,
      column: values.column,
      level: values.level,
      slot: values.slot,
      positionAttributes: values.positionAttributes && {
        monoSku: values.positionAttributes.monoSku,
        monoBatch: values.positionAttributes.monoBatch,
        blockIngress: values.positionAttributes.blockIngress,
        blockEgress: values.positionAttributes.blockEgress,
        maxCapacity: values.positionAttributes.maxCapacity
      }
    }

    await callApi(updatePosition(payload))
      .then(() => {
        onSuccess?.('Posición actualizada correctamente')
      })
      .catch((error) => openSnackBar(error, false))
  }

  if (isLoading || apiState.status === Status.Loading) {
    return (
      <Box>
        <Typography>
          <Loading />
        </Typography>
      </Box>
    )
  }

  const formInitialValues = isEditing
    ? {
        ...editableItem,
        positionAttributes: editableItem!.positionAttributes ?? {
          ...defaultAttributes
        }
      }
    : initialValues

  return (
    <Fragment>
      <SnackBar
        message={message}
        status={isSnackSuccess ? 'success' : 'error'}
        open={isActive}
      />
      <Formik
        innerRef={formikRef}
        initialValues={formInitialValues}
        onSubmit={async (values) =>
          isEditing ? handleSubmitEdit(values) : handleSubmit(values)
        }
        validationSchema={positionValidationSchema(
          requiredZone,
          requiresLayout
        )}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form autoComplete='off'>
            <Grid container spacing={3}>
              {editableItem?.hasStockMovements && (
                <Grid size={12}>
                  <CustomAlert
                    severity='warning'
                    message='Esta posición tiene movimientos de stock registrados. Sólo puedes modificar la zona, para proteger la integridad del historial. Si ya no deseas usar esta posición, puedes inactivarla.'
                    maxWidth='100%'
                  />
                </Grid>
              )}
              {/* Columna 1: Datos de la posición */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  color='primary'
                  gutterBottom
                >
                  Datos de la posición
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <GenericDLL
                      fullWidth
                      initialValues={warehouses || []}
                      valueKey='id'
                      name='warehouseName'
                      title='Almacén'
                      label='Almacén'
                      variant='outlined'
                      value={values.warehouseId || -1}
                      disabled={isLocked}
                      SelectProps={isLocked ? { IconComponent: LockIcon } : undefined}
                      onChange={(e: any) => {
                        const { value } = e.target
                        setFieldValue('warehouseId', value)
                        handleSelectedWarehouseZones(value, zones!)
                        setFieldValue('zoneId', undefined)
                        setFieldValue('subZoneId', undefined)
                        setFieldValue('externalStorage', value)
                      }}
                    />
                    <p
                      style={{
                        color: '#f44336',
                        fontSize: '13px',
                        margin: '4px 0 0'
                      }}
                    >
                      <ErrorMessage name='warehouseId' />
                    </p>
                  </Grid>
                  <Grid size={12}>
                    <GenericDLL
                      fullWidth
                      initialValues={zonesDDL}
                      valueKey='zoneId'
                      name='zoneName'
                      title='Zona'
                      label='Zona'
                      variant='outlined'
                      value={values.zoneId || -1}
                      onChange={(e: any) => {
                        const val =
                          e.target.value === -1 ? undefined : e.target.value
                        handleZoneChange(val, setFieldValue, zonesDDL)
                      }}
                    />
                    <p
                      style={{
                        color: '#f44336',
                        fontSize: '13px',
                        margin: '4px 0 0'
                      }}
                    >
                      <ErrorMessage name='zoneId' />
                    </p>
                  </Grid>
                  <Grid size={12}>
                    <GenericDLL
                      fullWidth
                      initialValues={typePositionItems.filter(
                        ({ typePositionId, canCreatePosition }) =>
                          shouldShowTypePosition(
                            !!values.externalStorage,
                            typePositionId,
                            canCreatePosition
                          )
                      )}
                      valueKey='typePositionId'
                      name='typePositionDescription'
                      title='Tipo Posición'
                      label='Tipo Posición'
                      variant='outlined'
                      value={values.typePositionId || -1}
                      disabled={isLocked}
                      SelectProps={isLocked ? { IconComponent: LockIcon } : undefined}
                      onChange={(e: any) => {
                        const { value } = e.target
                        validateSelectedTypePosition(value)
                        setFieldValue('typePositionId', value)
                        setFieldValue('positionAttributes', {
                          ...defaultAttributes
                        })
                      }}
                    />
                    <p
                      style={{
                        color: '#f44336',
                        fontSize: '13px',
                        margin: '4px 0 0'
                      }}
                    >
                      <ErrorMessage name='typePositionId' />
                    </p>
                  </Grid>
                  <Grid size={12}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography
                      variant='subtitle2'
                      fontWeight={700}
                      color='primary'
                      gutterBottom
                    >
                      Estado
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5
                      }}
                    >
                      <Typography variant='body2'>Posición activa</Typography>
                      <Switch
                        checked={!!values.active}
                        onChange={(e) =>
                          setFieldValue('active', e.target.checked)
                        }
                        size='small'
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Columna 2: Atributos físicos + Código + Descripción */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  color='primary'
                  gutterBottom
                >
                  Atributos físicos
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <MuiTextField
                      fullWidth
                      label='Pasillo'
                      variant='outlined'
                      size='small'
                      value={values.aisle || ''}
                      inputProps={{ maxLength: 3 }}
                      error={touched.aisle && Boolean(errors.aisle)}
                      helperText={touched.aisle ? errors.aisle : undefined}
                      onChange={(e) => {
                        const val = e.target.value
                          .toUpperCase()
                          .replace(/\s+/g, '')
                        setFieldValue('aisle', val)
                        if (!isLocked) {
                          setFieldValue(
                            'positionName',
                            buildPositionName(
                              val,
                              values.column || '',
                              values.level || '',
                              values.slot || ''
                            )
                          )
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <MuiTextField
                      fullWidth
                      label='Columna'
                      variant='outlined'
                      size='small'
                      value={values.column || ''}
                      inputProps={{ maxLength: 3 }}
                      error={touched.column && Boolean(errors.column)}
                      helperText={touched.column ? errors.column : undefined}
                      onChange={(e) => {
                        const val = e.target.value
                          .toUpperCase()
                          .replace(/\s+/g, '')
                        setFieldValue('column', val)
                        if (!isLocked) {
                          setFieldValue(
                            'positionName',
                            buildPositionName(
                              values.aisle || '',
                              val,
                              values.level || '',
                              values.slot || ''
                            )
                          )
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <MuiTextField
                      fullWidth
                      label='Nivel'
                      variant='outlined'
                      size='small'
                      value={values.level || ''}
                      inputProps={{ maxLength: 3 }}
                      error={touched.level && Boolean(errors.level)}
                      helperText={touched.level ? errors.level : undefined}
                      onChange={(e) => {
                        const val = e.target.value
                          .toUpperCase()
                          .replace(/\s+/g, '')
                        setFieldValue('level', val)
                        if (!isLocked) {
                          setFieldValue(
                            'positionName',
                            buildPositionName(
                              values.aisle || '',
                              values.column || '',
                              val,
                              values.slot || ''
                            )
                          )
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={6}>
                    <MuiTextField
                      fullWidth
                      label='Slot'
                      variant='outlined'
                      size='small'
                      value={values.slot || ''}
                      inputProps={{ maxLength: 3 }}
                      error={touched.slot && Boolean(errors.slot)}
                      helperText={touched.slot ? errors.slot : undefined}
                      onChange={(e) => {
                        const val = e.target.value
                          .toUpperCase()
                          .replace(/\s+/g, '')
                        setFieldValue('slot', val)
                        if (!isLocked) {
                          setFieldValue(
                            'positionName',
                            buildPositionName(
                              values.aisle || '',
                              values.column || '',
                              values.level || '',
                              val
                            )
                          )
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <MuiTextField
                      fullWidth
                      label='Nombre'
                      variant='outlined'
                      size='small'
                      value={values.positionName || ''}
                      placeholder='Se generará automáticamente'
                      disabled={isLocked}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: isLocked && (
                          <InputAdornment position='end'>
                            <LockIcon fontSize='small' color='disabled' />
                          </InputAdornment>
                        )
                      }}
                      inputProps={{
                        maxLength: 50,
                        style: { textTransform: 'uppercase' }
                      }}
                      error={
                        touched.positionName && Boolean(errors.positionName)
                      }
                      helperText={
                        touched.positionName ? errors.positionName : undefined
                      }
                      onChange={(e) =>
                        setFieldValue(
                          'positionName',
                          e.target.value.toUpperCase().replace(/\s+/g, '')
                        )
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    <Field
                      fullWidth
                      name='positionDescription'
                      component={TextField}
                      label='Descripción de la posición'
                      multiline
                      rows={3}
                      variant='outlined'
                      placeholder='Ingrese una descripción detallada de la posición...'
                      disabled={isLocked}
                      InputProps={{
                        endAdornment: isLocked && (
                          <InputAdornment position='end'>
                            <LockIcon fontSize='small' color='disabled' />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Columna 3: Atributos operativos */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  color='primary'
                  gutterBottom
                >
                  Atributos operativos
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 2,
                    py: 0.5
                  }}
                >
                  <SwitchRow
                    label='Monosku'
                    description='Solo permite un SKU en esta posición'
                    checked={!!values.positionAttributes?.monoSku}
                    onChange={(v) =>
                      setFieldValue('positionAttributes.monoSku', v)
                    }
                  />
                  <Divider />
                  <SwitchRow
                    label='Monolote'
                    description='Solo permite un lote por vez'
                    checked={!!values.positionAttributes?.monoBatch}
                    onChange={(v) =>
                      setFieldValue('positionAttributes.monoBatch', v)
                    }
                  />
                  <Divider />
                  <SwitchRow
                    label='Bloquear ingreso'
                    description='Impide el ingreso de productos'
                    checked={!!values.positionAttributes?.blockIngress}
                    onChange={(v) =>
                      setFieldValue('positionAttributes.blockIngress', v)
                    }
                  />
                  <Divider />
                  <SwitchRow
                    label='Bloquear egreso'
                    description='Impide el egreso de productos'
                    checked={!!values.positionAttributes?.blockEgress}
                    onChange={(v) =>
                      setFieldValue('positionAttributes.blockEgress', v)
                    }
                  />
                  <Divider />
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1.5
                    }}
                  >
                    <Box>
                      <Typography variant='body2' fontWeight={600}>
                        Capacidad máxima
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        Cantidad máxima de unidades permitidas
                      </Typography>
                    </Box>
                    <MuiTextField
                      type='number'
                      variant='outlined'
                      size='small'
                      value={values.positionAttributes?.maxCapacity ?? 0}
                      error={Boolean(
                        (errors.positionAttributes as any)?.maxCapacity
                      )}
                      helperText={
                        (errors.positionAttributes as any)?.maxCapacity
                      }
                      inputProps={{
                        min: 0,
                        max: 2147483647,
                        style: { width: 64, textAlign: 'center' }
                      }}
                      onChange={(e) =>
                        setFieldValue(
                          'positionAttributes.maxCapacity',
                          Number(e.target.value)
                        )
                      }
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Fragment>
  )
})
